import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ENVIRONMENT } from '@cfp/core';
import { AlertService } from '@cfp/ui/feedback';
import { TranslateService } from '@ngx-translate/core';
import { ContactService, OnboardingService } from 'csr-login-app';
import { mergeMap } from 'rxjs';
import { isBsatRegistrationClosed } from '../bsat-registration-closed';
import { SelfOnboardingCreateAccount } from '../self-onboarding-create-account';
import { SelfOnboardingCreateAccountData } from '../self-onboarding-create-account-data';
import { SelfOnboardingLayout } from '../self-onboarding-layout';
import { SelfOnboardingRegistrationClosed } from '../self-onboarding-registration-closed';

@Component({
  selector: 'cfp-self-onboarding-create-account-page',
  templateUrl: './self-onboarding-create-account-page.html',
  imports: [SelfOnboardingCreateAccount, SelfOnboardingLayout, SelfOnboardingRegistrationClosed],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelfOnboardingCreateAccountPage {
  protected readonly isLoading = signal(false);

  private readonly environment = inject(ENVIRONMENT);
  private readonly router = inject(Router);
  private readonly onboardingService = inject(OnboardingService);
  private readonly alertService = inject(AlertService);
  private readonly contactService = inject(ContactService);
  private readonly translateService = inject(TranslateService);

  protected readonly isRegistrationClosed = isBsatRegistrationClosed(this.environment.environmentCode.dto);
  protected readonly showSector = this.environment.selfOnboarding?.showSector ?? false;
  protected readonly showCountryOfIncorporation = this.environment.selfOnboarding?.showCountryOfIncorporation ?? false;

  protected previous(): void {
    this.router.navigateByUrl(this.environment.loginRoute);
  }

  protected next(data: SelfOnboardingCreateAccountData): void {
    this.isLoading.set(true);
    this.onboardingService
      .v1OnboardingCreateuserPost({
        environmentCode: this.environment.environmentCode.dto,
        userEmail: data.companyEmail,
        userName: data.contactName,
        customerName: data.companyName,
        countryCode: data.countryOfIncorporation,
        relationManagerName: data.relationManagerName,
        sector: data.sector
      })
      .pipe(
        mergeMap(() => {
          const details: { [key: string]: string } = {
            [this.translateService.instant('selfOnboarding.createAccount.companyName')]: data.companyName,
            [this.translateService.instant('selfOnboarding.createAccount.contactName')]: data.contactName
          };

          if (this.showCountryOfIncorporation) {
            details[this.translateService.instant('selfOnboarding.createAccount.countryOfIncorporation')] =
              data.countryOfIncorporation || '-';
          }

          if (this.showSector) {
            details[this.translateService.instant('selfOnboarding.createAccount.sector')] = data.sector || '-';
          }

          details[this.translateService.instant('selfOnboarding.createAccount.relationManagerName')] =
            data.relationManagerName || '-';

          return this.contactService.v2ContactsupportPost({
            environmentCode: this.environment.environmentCode.dto,
            email: data.companyEmail,
            message: this.translateService.instant('selfOnboarding.createAccount.message'),
            subject: this.translateService.instant('selfOnboarding.createAccount.subject', {
              country: data.countryOfIncorporation
            }),
            details: details,
            skipCopyToUser: true
          });
        })
      )
      .subscribe({
        next: () => this.router.navigateByUrl('/create-account/verify'),
        error: () => {
          this.isLoading.set(false);
          this.alertService.showAlert('error', 'onboarding.error.general', 'onboarding.error.tryLogin');
        }
      });
  }
}
